import { css } from '@emotion/react';

import { useRouter } from 'next/router';

import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { media } from '@styles/css';
import { CurationViewType, useAnalytics } from '@utils/analytics';

import { fetchBanners } from '../../api/common';
import { IBanner, IEFinderObject } from '../../model';
import { useWebView } from '../../utils/webview';
import Border, { BorderType } from '../layout/Border';
import { EFinderImageSizes, OptimizeEFinderImage } from './EFinderImage';

export const enum BannerSize {
  curation = '38.8889%',
  promotion = '20%',
  home = '56.25%',
  playHome = '48%',
}

export interface BannerProps {
  name: string;
  size?: BannerSize;
  items: IBanner[];
  isBorder?: boolean;
  priority?: boolean;
  autoPlay?: boolean;
  allowVideo?: boolean;
}

const bannerItemCSS = (size: number | string) => css`
  position: relative;
  padding-bottom: ${typeof size === 'number' ? `${size}px` : size};
`;

const videoCSS = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const responsiveBannerCSS = css`
  ${media.tablet} {
    max-width: 500px;
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;
  }
  ${media.desktop} {
    max-width: 560px;
    border-radius: 16px;
  }
`;

type BannerVideoProps = {
  active: boolean;
  autoPlay: boolean;
  loop: boolean;
  onEnded: () => void;
  poster?: string;
  video: IEFinderObject<'video'>;
};

const BannerVideo = ({
  active,
  autoPlay,
  loop,
  onEnded,
  poster,
  video,
}: BannerVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);
  const src = video.attachment?.url;

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    if (!active || !autoPlay) {
      element.pause();
      return;
    }

    if (element.ended) {
      element.currentTime = 0;
    }

    const playPromise = element.play();
    if (playPromise) {
      playPromise.catch(() => undefined);
    }
  }, [active, autoPlay, src]);

  if (!src) {
    return null;
  }

  return (
    <video
      ref={ref}
      css={videoCSS}
      src={src}
      poster={poster}
      muted
      playsInline
      autoPlay={active && autoPlay}
      loop={loop}
      onEnded={onEnded}
      preload={active ? 'auto' : 'metadata'}
    />
  );
};

const getBannerHeight = (item: IBanner) =>
  (item.image?.attachment?.height ?? item.video?.attachment?.height ?? 150) / 3;

const hasVideoSource = (item: IBanner) => !!item.video?.attachment?.url;

const Banner = ({
  name,
  isBorder = true,
  size,
  items,
  priority = false,
  autoPlay = true,
  allowVideo = false,
}: BannerProps) => {
  const { platform } = useGlobalContext();
  const { web } = useWebView();
  const { firebase } = useAnalytics();
  const router = useRouter();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= items.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, items.length]);

  if (items.length === 0) {
    return null;
  }

  const activeItem = items[activeIndex];
  const activeVideoAutoAdvance =
    autoPlay &&
    allowVideo &&
    items.length > 1 &&
    activeItem?.mediaType === 'video' &&
    hasVideoSource(activeItem);
  const carouselAutoPlay = autoPlay && !activeVideoAutoAdvance;

  return (
    <>
      {isBorder && <Border type={BorderType.space} />}
      <Carousel
        css={responsiveBannerCSS}
        autoPlay={carouselAutoPlay}
        selectedItem={activeIndex}
        showIndicators={items.length > 1}
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        infiniteLoop
        emulateTouch
        onChange={setActiveIndex}
        onClickItem={(i) => {
          if (name === 'play-full-screen=banner') {
            firebase.logEvent('chatbot_click_play_curation', {
              curation_view_type: 'full_screen_banner' as CurationViewType,
            });
          }
          const { url } = items[i];
          if (url) {
            if (platform === Platform.web) {
              router.push(url);
            }
            web.openUrl(url);
          }
        }}
      >
        {items.map((item, idx) => {
          const image = item.image;
          const video =
            allowVideo && item.mediaType === 'video' ? item.video : undefined;
          const isActiveVideoAutoAdvance =
            activeVideoAutoAdvance && idx === activeIndex;

          return (
            <div
              key={`banner-${name}-${item.id}`}
              css={bannerItemCSS(size ?? getBannerHeight(item))}
            >
              {video?.attachment?.url ? (
                <BannerVideo
                  active={idx === activeIndex}
                  autoPlay={autoPlay}
                  loop={!isActiveVideoAutoAdvance}
                  onEnded={() => {
                    if (!autoPlay || items.length <= 1) {
                      return;
                    }

                    setActiveIndex((currentIndex) => {
                      if (currentIndex !== idx) {
                        return currentIndex;
                      }

                      return (idx + 1) % items.length;
                    });
                  }}
                  poster={image?.attachment?.url}
                  video={video}
                />
              ) : (
                image && (
                  <OptimizeEFinderImage
                    image={image}
                    h={EFinderImageSizes.banner}
                    loading={idx === 0 && priority ? 'eager' : 'lazy'}
                    priority={idx === 0 && priority}
                  />
                )
              )}
            </div>
          );
        })}
      </Carousel>
    </>
  );
};

export default Banner;

export const enum BannerKey {
  story = 'story',
  myStory = 'my_story',
}

type KeyBannerProps = {
  bKey: BannerKey | string;
  size?: BannerSize;
};

export const KeyBanner = ({ bKey, size }: KeyBannerProps) => {
  const query = useQuery(['banners', bKey], () => fetchBanners(bKey), {
    suspense: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const { data } = query;

  if (!data) {
    return null;
  }

  return <Banner name={bKey} size={size} items={data} />;
};
